import type {
  AdminFoodRequestDecisions,
  AdminFoodRequestDraft,
  AdminFoodRequestFood,
  AdminFoodRequestField,
  FoodMacrosView,
  PortionUnitName,
} from '@steado/admin-contract/foods';

import { MACRO_TOLERANCE } from '@/screens/food-detail/food-edit-model';

import { figure } from './request-copy';

/**
 * A fix, row by row. The API decides nutrition as one group of five and the
 * portion set as one; the design decides each value the research would
 * change, Keep current or Use new, with nothing chosen for the reviewer. This
 * is the mapping between the two: the rows a draft makes, against the food
 * as it is now, and the one decision per group the rows add up to.
 */

export type Choice = 'keep' | 'new';
export type Choices = Readonly<Record<string, Choice>>;

type MacroKey = keyof FoodMacrosView;

const MACROS: readonly { key: MacroKey; label: string; short: string; unit: 'kcal' | 'g' }[] = [
  { key: 'kcal', label: 'Energy per 100 g', short: 'Energy', unit: 'kcal' },
  { key: 'proteinG', label: 'Protein per 100 g', short: 'Protein', unit: 'g' },
  { key: 'carbsG', label: 'Carbohydrate per 100 g', short: 'Carbohydrate', unit: 'g' },
  { key: 'fatG', label: 'Fat per 100 g', short: 'Fat', unit: 'g' },
  { key: 'fibreG', label: 'Fibre per 100 g', short: 'Fibre', unit: 'g' },
];

interface Portion {
  readonly label: string;
  readonly unit: PortionUnitName;
  readonly grams: number;
  readonly isDefault: boolean;
}

export interface FixRow {
  readonly id: string;
  readonly group: 'nutrition' | 'portions';
  readonly label: string;
  /** The name the status line uses for an open row: "Energy, Portion open". */
  readonly short: string;
  readonly unit: 'kcal' | 'g';
  /** The food as it is now; null for a portion it does not have. */
  readonly current: number | null;
  /** What it was when the research ran, when that differs from now. */
  readonly drafted: number | null;
  /** What the research proposes; null for a portion it would remove. */
  readonly proposed: number | null;
  readonly change: string;
  /** For a portion row: what it is a portion of. */
  readonly portion?: Portion;
  readonly field: AdminFoodRequestField | undefined;
}

const near = (a: number | null, b: number | null): boolean =>
  a === null || b === null ? a === b : Math.abs(a - b) < 0.05;

function changeOf(current: number | null, proposed: number | null): string {
  if (current === null) return 'Added';
  if (proposed === null) return 'Removed';
  if (current === 0) return proposed === 0 ? '0%' : 'New';
  const pct = Math.round(((proposed - current) / current) * 100);
  return pct > 0 ? `+${String(pct)}%` : pct < 0 ? `−${String(-pct)}%` : '0%';
}

const keyOf = (portion: { label: string; unit: string }) =>
  `${portion.unit}|${portion.label.trim().toLowerCase()}`;

/** The rows a fix's draft makes: each value it would change, against the food as it is now. */
export function fixRows(draft: AdminFoodRequestDraft, food: AdminFoodRequestFood): FixRow[] {
  const rows: FixRow[] = [];
  const nutrition = draft.fields.find((field) => field.field === 'nutrition');
  const proposedMacros = draft.proposed?.macrosPer100g ?? null;
  const draftedMacros = draft.current?.macrosPer100g ?? null;
  if (nutrition !== undefined && proposedMacros !== null) {
    for (const macro of MACROS) {
      const current = food.macrosPer100g[macro.key];
      const proposed = proposedMacros[macro.key];
      const drafted = draftedMacros === null ? current : draftedMacros[macro.key];
      if (near(drafted, proposed) && near(current, proposed)) continue;
      rows.push({
        id: macro.key,
        group: 'nutrition',
        label: macro.label,
        short: macro.short,
        unit: macro.unit,
        current,
        drafted: near(drafted, current) ? null : drafted,
        proposed,
        change: changeOf(current, proposed),
        field: nutrition,
      });
    }
  }

  const portions = draft.fields.find((field) => field.field === 'portions');
  const proposedPortions = draft.proposed?.portions ?? [];
  if (portions !== undefined && proposedPortions.length > 0) {
    const live = new Map(food.portions.map((portion) => [keyOf(portion), portion]));
    const kept = new Set<string>();
    for (const portion of proposedPortions) {
      const key = keyOf(portion);
      kept.add(key);
      const existing = live.get(key);
      if (
        existing !== undefined &&
        near(existing.grams, portion.grams) &&
        existing.isDefault === portion.isDefault
      )
        continue;
      rows.push({
        id: `portion:${key}`,
        group: 'portions',
        label: portion.isDefault
          ? `Default portion, 1 ${portion.label}`
          : existing === undefined
            ? `New portion, 1 ${portion.label}`
            : `Portion, 1 ${portion.label}`,
        short: portion.isDefault ? 'Portion' : `${capitalise(portion.label)} portion`,
        unit: 'g',
        current: existing?.grams ?? null,
        drafted: null,
        proposed: portion.grams,
        change: changeOf(existing?.grams ?? null, portion.grams),
        portion,
        field: portions,
      });
    }
    for (const portion of food.portions) {
      if (kept.has(keyOf(portion))) continue;
      rows.push({
        id: `portion:${keyOf(portion)}`,
        group: 'portions',
        label: `${portion.isDefault ? 'Default portion' : 'Portion'}, 1 ${portion.label}`,
        short: `${capitalise(portion.label)} portion`,
        unit: 'g',
        current: portion.grams,
        drafted: null,
        proposed: null,
        change: 'Removed',
        portion,
        field: portions,
      });
    }
  }
  return rows;
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function valueText(row: FixRow, value: number | null): string {
  if (value === null) return row.proposed === null && row.current !== null ? 'Removed' : 'None';
  return `${figure(value)} ${row.unit}`;
}

/** The figures per 100 g with the reviewer's choices so far: a row not taken stays as it is now. */
export function chosenMacros(rows: readonly FixRow[], choices: Choices, food: AdminFoodRequestFood) {
  const macros = { ...food.macrosPer100g };
  for (const row of rows) {
    if (row.group !== 'nutrition' || choices[row.id] !== 'new' || row.proposed === null) continue;
    macros[row.id as MacroKey] = row.proposed;
  }
  return macros;
}

/** The portion set with the reviewer's choices: the food's, with each row taken applied to it. */
export function chosenPortions(
  rows: readonly FixRow[],
  choices: Choices,
  food: AdminFoodRequestFood,
): Portion[] {
  let set: Portion[] = food.portions.map((portion) => ({ ...portion }));
  let defaultKey: string | null = null;
  for (const row of rows) {
    if (row.group !== 'portions' || row.portion === undefined || choices[row.id] !== 'new') continue;
    const key = keyOf(row.portion);
    if (row.proposed === null) {
      set = set.filter((portion) => keyOf(portion) !== key);
      continue;
    }
    const taken: Portion = { ...row.portion, grams: row.proposed };
    const at = set.findIndex((portion) => keyOf(portion) === key);
    if (at === -1) set.push(taken);
    else set[at] = taken;
    if (taken.isDefault) defaultKey = key;
  }
  // One default: the one a taken row made default, else the food's, else the first.
  if (defaultKey !== null)
    set = set.map((portion) => ({ ...portion, isDefault: keyOf(portion) === defaultKey }));
  if (!set.some((portion) => portion.isDefault) && set[0] !== undefined)
    set[0] = { ...set[0], isDefault: true };
  return set;
}

/** The approval's decisions: one per group, `keep` when every row of it is kept. */
export function fixDecisions(
  rows: readonly FixRow[],
  choices: Choices,
  food: AdminFoodRequestFood,
): AdminFoodRequestDecisions {
  const decisions: AdminFoodRequestDecisions = {};
  const nutrition = rows.filter((row) => row.group === 'nutrition');
  if (nutrition.length > 0) {
    decisions.nutrition = nutrition.every((row) => choices[row.id] === 'keep')
      ? { keep: true }
      : { value: chosenMacros(rows, choices, food) };
  }
  const portions = rows.filter((row) => row.group === 'portions');
  if (portions.length > 0) {
    decisions.portions = portions.every((row) => choices[row.id] === 'keep')
      ? { keep: true }
      : { value: chosenPortions(rows, choices, food) };
  }
  return decisions;
}

/** Protein, carbohydrate and fat as energy: 4, 4 and 9 kcal a gram. */
export function macroKcal(macros: { proteinG: number; carbsG: number; fatG: number }): number {
  return macros.proteinG * 4 + macros.carbsG * 4 + macros.fatG * 9;
}

/** How far energy is from what the macros add up to, as a fraction of the macros. */
export function macroGap(kcal: number, macros: { proteinG: number; carbsG: number; fatG: number }): number {
  const sum = macroKcal(macros);
  return sum === 0 ? 0 : (kcal - sum) / sum;
}

export interface FixStatus {
  readonly decided: number;
  readonly total: number;
  readonly open: readonly FixRow[];
  readonly allDecided: boolean;
  /** Energy and the macros, as chosen, too far apart for the catalog to take. */
  readonly gapTooWide: boolean;
  readonly ready: boolean;
  /** Every row kept: approving would change nothing, which the API refuses. */
  readonly nothingChanges: boolean;
  readonly text: string;
  readonly tone: 'ok' | 'warn' | 'err';
  /** Why the apply button waits, for its `aria-describedby`. Null when it does not. */
  readonly why: string | null;
}

export function fixStatus(
  rows: readonly FixRow[],
  choices: Choices,
  food: AdminFoodRequestFood,
  stale: boolean,
): FixStatus {
  const open = rows.filter((row) => choices[row.id] === undefined);
  const decided = rows.length - open.length;
  const allDecided = open.length === 0;
  const macros = chosenMacros(rows, choices, food);
  const gap = Math.abs(macroGap(macros.kcal, macros));
  const touchesNutrition = rows.some((row) => row.group === 'nutrition');
  const gapTooWide = allDecided && touchesNutrition && gap > MACRO_TOLERANCE;
  const nothingChanges = allDecided && rows.every((row) => choices[row.id] === 'keep');
  const name = food.displayName ?? food.name;
  const total = rows.length;

  let text: string;
  let tone: FixStatus['tone'];
  if (stale) {
    text = `${name} changed since this was drafted · compare with ${name} now to apply`;
    tone = 'err';
  } else if (gapTooWide) {
    text = `Energy and the macros are ${String(Math.round(gap * 100))}% apart · change one to apply`;
    tone = 'err';
  } else if (allDecided) {
    text = `All ${String(total)} decided`;
    tone = 'ok';
  } else if (open.length <= 3) {
    text = `${String(decided)} of ${String(total)} decided · ${open.map((row) => row.short).join(', ')} open`;
    tone = 'warn';
  } else {
    text = `${String(decided)} of ${String(total)} decided · ${String(open.length)} open`;
    tone = 'warn';
  }

  const why = stale
    ? `${name} changed since this was drafted. Compare with ${name} now first.`
    : gapTooWide
      ? 'Energy and the macros are too far apart. Change one first.'
      : allDecided
        ? null
        : `Decide every field first: ${open.map((row) => row.short).join(', ')}.`;

  return {
    decided,
    total,
    open,
    allDecided,
    gapTooWide,
    ready: why === null,
    nothingChanges,
    text,
    tone,
    why,
  };
}
